<template>
    <BButton class="omnistock omni-btn"
             :class="computedClassNames"
             :disabled="disabled"
             @click="onClick"
             v-b-popover.hover.top="hoverPopupText"
             v-bind="[$attrs, $props]"
             :variant="variant"
    >

        <template v-if="iconOnly && hasIcon">
            <slot name="icon">
                <FontAwesomeIcon :icon="icon" :spin="iconSpin" v-if="!isProcessing"/>
                <FontAwesomeIcon icon="spinner" spin v-else />
            </slot>
        </template>

        <template v-else>
            <slot/>
            <FontAwesomeIcon v-if="isProcessing" icon="spinner" spin class="d-inline-block ml-2"/>
        </template>
    </BButton>
</template>

<style lang="scss">
.omnistock {
    &.omni-btn {
        /**
         * Variables
         */
        $btn-bg-color: #f4f7fa;
        $btn-border-color: #dadada;
        $btn-text-color: rgb(88, 91, 105);
        $btn-text-color-hover: #000;
        $btn-text-color-disabled: #6c757d;
        $btn-border-radius: .25rem;
        $hover-darken-amount: 5%;

        /**
         * Layout
         */
        padding: .5rem 1.25rem;
        white-space: nowrap;
        vertical-align: middle;

        /**
         * Box Model
         */
        background-color: $btn-bg-color;
        border-radius: $btn-border-radius;
        border-width: 1px;
        border-style: solid;
        border-color: $btn-border-color;

        /**
         * Font & Text Style
         */
        font-size: .8rem;
        line-height: 1.5;
        text-align: center;
        color: $btn-text-color;
        font-weight: 700;

        /**
         * Interactivity
         */
        cursor: pointer;
        user-select: none;

        /**
         * Hover State
         */
        &:hover {
            background-color: darken($btn-bg-color, $hover-darken-amount);
            border-color: darken($btn-border-color, $hover-darken-amount);
            color: $btn-text-color-hover;
        }

        /**
         * Focus State
         */
        &:focus {
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.5);
        }

        /**
         * Disabled State
         */
        &:disabled {
            opacity: .65;
            cursor: not-allowed;
            color: $btn-text-color-disabled;
            background: #ededed;
            border: 1px solid #dadada;
        }

        &.btn-variant-save, &.btn-variant-success {
            background-color: #28a745;
            border-color: #28a745;
            color: #fff;

            &:hover {
                background-color: darken(#28a745, $hover-darken-amount);
                border-color: darken(#28a745, $hover-darken-amount);
                color: #fff;
            }
        }

        &.btn-variant-secondary {
            background-color: #007bff !important;
            border-color: #007bff !important;
            color: #fff;

            &:hover {
                background-color: #0069d9 !important;;
                border-color: #0069d9 !important;;
                color: #fff;
            }
        }

        &.btn-variant-danger {
            background-color: #fff2f2;
            border-color: #dc3545;
            color: #e44f4f;

            &:hover {
                background-color: darken(#dc3545, $hover-darken-amount);
                border-color: darken(#dc3545, $hover-darken-amount);
                color: #fff;
            }
        }

        &.icon-only {
            padding: 0;
            background: unset;
            border: none;

            &[size="lg"] { font-size: 1rem; }

            &:focus, &:focus-within {
                color: initial;
            }

            &.btn-variant-danger {
                &:hover {
                    background: none;
                    color: #e44f4f;
                }
            }

            &.btn-variant-success {
                color: #28a745;

                &:hover {
                    background: none;
                    color: darken(#28a745, 10%);
                }
                &:disabled {
                    color: #333 !important;
                }
            }

            &.btn-variant-info {
                color: #007bff;

                &:hover {
                    background: none;
                    color: darken(#007bff, 10%);
                }
                &:disabled {
                    color: #333 !important;
                }
            }
        }
    }
}


</style>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "Button",
    props: {
        variant: {
            type: String,
            default: "primary",
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
            required: false,
        },
        icon: {
            type: String,
            default: '',
            required: false,
        },
        iconSpin: {
            type: Boolean,
            default: false,
            required: false,
        },
        title: {
            type: String,
            default: '',
            required: false,
        },
        isProcessing: {
            type: Boolean,
            default: false,
            required: false,
        }
    },
    methods: {
        onClick() {
            this.$emit("click");
        },
    },
    computed: {
        /**
         * Computes the class names based on the `variant` prop.
         * @returns {Array<string>} An array of class names for the component.
         */
        computedClassNames() {
            return [
                `btn-variant-${this.variant}`,
                this.iconOnly && this.hasIcon ? 'icon-only' : undefined,
            ];
        },

        /**
         * Determines whether the button display mode is set to 'icons'.
         * @returns {boolean} True if only icons should be shown, false otherwise.
         */
        iconOnly() {
            return this.$store.getters.getFrontendParameterValue('general', 'button-display-mode') === 'icons';
        },

        /**
         * Decides if an icon is available for the button. Used for template rendering logic.
         * @returns {boolean} True if an icon or an icon slot is present, false otherwise.
         */
        hasIcon() {
            return this.icon || this.$slots.icon;
        },

        /**
         * Computes the text to display in the hover popup when the button is in icons-only mode.
         * @returns {string} The text for the hover popup or an empty string if no text is available.
         */
        hoverPopupText() {
            if (!this.iconOnly || this.title) {
                return '';
            }

            return this.$slots.default ? this.$slots.default[0].text : '';
        }
    },
});
</script>
